import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { DiscountKind, ModelDiscountSummary } from '@fetmode/types';
import { DISCOUNT_KINDS } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type DiscountDraft = {
  kind: DiscountKind;
  label: string;
  percentOff: string;
  target: string;
};

const emptyDraft = (): DiscountDraft => ({
  kind: 'TIME_OF_DAY',
  label: '',
  percentOff: '10',
  target: '',
});

const KIND_LABELS: Record<DiscountKind, string> = {
  TIME_OF_DAY: 'common.studio.discounts.kindTimeOfDay',
  GROUP: 'common.studio.discounts.kindGroup',
  INDIVIDUAL: 'common.studio.discounts.kindIndividual',
  COUPON: 'common.studio.discounts.kindCoupon',
};

export function ModelDiscountsCard(): ReactElement {
  const { api } = useAuth();
  const [discounts, setDiscounts] = useState<ModelDiscountSummary[]>([]);
  const [draft, setDraft] = useState<DiscountDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listMyDiscounts();
    setDiscounts(result.discounts);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.discounts.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleAdd(): Promise<void> {
    const percentOff = Number.parseInt(draft.percentOff, 10);
    if (!draft.label.trim() || !Number.isFinite(percentOff) || percentOff < 1 || percentOff > 90) {
      setError(t('common.studio.discounts.invalidDiscount'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createDiscount({
        kind: draft.kind,
        label: draft.label.trim(),
        percentOff,
        target: draft.target.trim() || null,
      });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.discounts.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleEnabled(discount: ModelDiscountSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateDiscount(discount.id, { enabled: !discount.enabled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.discounts.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(discountId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteDiscount(discountId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.discounts.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.discounts.loading')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.discounts.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.discounts.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {discounts.map((discount) => (
          <li
            key={discount.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
              opacity: discount.enabled ? 1 : 0.55,
            }}
          >
            <span style={{ flex: '1 1 10rem', fontWeight: 600 }}>
              {discount.label} · {t(KIND_LABELS[discount.kind])} · {discount.percentOff}%
              {discount.target ? ` · ${discount.target}` : ''}
            </span>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={discount.enabled}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleEnabled(discount);
                }}
              />
              {t('common.studio.discounts.enabled')}
            </label>
            <Button
              label={t('common.studio.discounts.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(discount.id);
              }}
            />
          </li>
        ))}
      </ul>

      {discounts.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.discounts.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
        <label>
          {t('common.studio.discounts.kind')}
          <select
            className="fm-input"
            value={draft.kind}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, kind: event.target.value as DiscountKind });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          >
            {DISCOUNT_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(KIND_LABELS[kind])}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('common.studio.discounts.label')}
          <input
            className="fm-input"
            value={draft.label}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, label: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.discounts.percentOff')}
          <input
            className="fm-input"
            type="number"
            min={1}
            max={90}
            value={draft.percentOff}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, percentOff: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.discounts.target')}
          <input
            className="fm-input"
            value={draft.target}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, target: event.target.value });
            }}
            placeholder={t('common.studio.discounts.targetPlaceholder')}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <Button
          label={isSaving ? t('common.studio.discounts.saving') : t('common.studio.discounts.add')}
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
